import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { IAppState } from 'core/store/store'
import { withPermissionsHOC } from 'core/hoc/permissionsHoc'
import { checkDataIntegrity, fetchAuditEvents } from './AuditLogs.actions'
import { Translate } from 'react-localize-redux'
import styles from './AuditLogs.module.scss'
import {
  DateRangeFilter,
  FreeTextFilter,
  SelectFilter,
  Table,
  TableColumn,
  TableMetaProps,
  Tag,
  TagType,
  Tooltip,
  Button,
  IToastNotification,
  ToasterContainer,
  ToasterMessageType
} from '@digicert/dcone-common-ui'
import { IAuditLogsEvent, IAuditLogsProps, IAuditLogsStateProps } from './AuditLogs.types'
import { LIMIT_10, LIMIT_MAX } from '../../../core/utils/constants'
import { formatDateWithTimestamp, translate } from '../../../shared/helpers/utils'
import { BasePathConst } from '../../../configs/Path.configs'
import { Link } from 'react-router-dom'
import { ISort } from '../../../core/actions/Actions.interfaces'
import isEqual from 'fast-deep-equal'
import moment from 'moment'
import { AuditLogActionItems, AuditLogResourceTypeItems, AuditLogStatusItems } from './AuditLogs.constants'
import { fetchTenants } from '../../tenants/Tenants.actions'
import { getItemFromLS } from 'shared/helpers/LocalStorage'
import { resizeTableBody } from '../../../shared/helpers/Table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileSearch } from '@fortawesome/pro-light-svg-icons'

const AuditLogs = (
  {
    fetchAuditEvents,
    fetchTenants,
    checkDataIntegrity,
    accounts,
    profile
  }: IAuditLogsProps,
): ReactElement<IAuditLogsStateProps> => {
  const storedTableData = JSON.parse(getItemFromLS('dcone-table/audit-table-' + profile?.id) as string)
  const defaultMeta: TableMetaProps<IAuditLogsEvent> = {
    pageSize: LIMIT_10,
    currentPage: 1,
    totalItems: 0,
    filters: getItemFromLS('dcone-table/audit-table-' + profile?.id) === null ? {} : storedTableData.meta.filters,
    sort: {
      columnKey: "timestamp",
      order: "descend"
    }
  }
  const [current, setCurrent] = useState(1)
  const [sortedInfo, setSortedInfo] = useState({columnKey: 'timestamp', order: 'descend'})
  const [meta, setMeta] = useState(defaultMeta)
  const [events, setEvents] = useState([{}] as IAuditLogsEvent[])
  const [allAccounts, setAllAccounts] = useState(accounts)
  const [loading, setLoading] = useState(true)
  const [notifications, setNotifications] = useState<IToastNotification[]>([])

  const sentenceCase = (string) => {
    const stringToTransform = string.toLowerCase().split(' ')
    for(let i = 0; i< stringToTransform.length; i++){
      stringToTransform[i] = stringToTransform[i][0].toUpperCase() + stringToTransform[i].slice(1);
    }
    return stringToTransform.join(' ');
  }

  function capitalizeRemoveUnderScores(string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase().replace(/_/g, ' ');
  }

  const parseAccounts = ():any => {
    const accountItems = allAccounts.map((account) => {
      return {
        label: account.display_name,
        value: account.id,
        func: 'eq'
      }
    })
    accountItems?.sort((a, b) => (a.label > b.label ? 1 : -1))
    return accountItems
  }

  const handleCheckDataIntergrity = (id: string) => {
    checkDataIntegrity(id).then(res => {
      if (res) {
        if (res.status === 'success') {
          setNotifications(
            [
              ...notifications,
              {
                type: ToasterMessageType.SUCCESS,
                description: translate('notifications.auditLog.verifySuccess'),
                id: 'auditLog-verify-success'
              }
            ]
          )
        } else {
          setNotifications(
            [
              ...notifications,
              {
                type: ToasterMessageType.ERROR,
                description: translate('notifications.auditLog.verifyFailed'),
                id: 'auditLog-verify-failed'
              }
            ]
          )
        }
      }
    })
  };

  const getTableColumns = (): TableColumn<IAuditLogsEvent>[] => [
    {
      key: 'timestamp',
      title: translate('common.date') as string,
      sorter: true,
      displayRowActions: true,
      render: event => (
        <>
        <Link to={`${BasePathConst}/reporting/logging/${event.id}`}>
          { profile && profile.date_format &&
          formatDateWithTimestamp(event.timestamp, profile.date_format)
          }
        </Link>
        </>
      ),
      renderFilter: function BasicNumberFilter(popupContainer) {
        return (
          <>
            <DateRangeFilter<IAuditLogsEvent>
              label='Timestamp'
              columnKey='timestamp'
              popupContainer={popupContainer}
            />
          </>
        );
      }
    },
    {
      key: 'userName',
      title: translate('reporting.auditEvents.userName') as string,
      sorter: true,
      render: event => (
        <>{event.user.name}</>
      ),
      renderFilter: function BasicStringFilter() {
        return (
            <FreeTextFilter<IAuditLogsEvent>
                label={translate('reporting.auditEvents.userName') as string}
                columnKey='userName'
            />
        );
      }
    },
    {
      key: 'account',
      title: translate('reporting.auditEvents.account') as string,
      render: event => (
        <>{event.account.name}</>
      ),
      renderFilter: function BasicStringFilter() {
        return (
          <SelectFilter
            multi={false}
            items={parseAccounts()}
            columnKey='account'
            label={translate('reporting.auditEvents.account') as string}
            minSearchLength={1}
          />
        )
      }
    },
    {
      key: 'action',
      title: translate('common.action') as string,
      render: event => (
        <>{capitalizeRemoveUnderScores(event.action)}</>
      ),
      renderFilter: function BasicStringFilter() {
        return (
          <SelectFilter
            multi={false}
            items={AuditLogActionItems()}
            columnKey='action'
            label={translate('common.action') as string}
            minSearchLength={1}
          />
        )
      }
    },
    {
      key: 'resource',
      title: translate('reporting.auditEvents.resource') as string,
      render: event => (
        <>{event.resource.name}</>
      )
    },
    {
      key: 'resource_type',
      title: translate('reporting.auditEvents.resourceType.title') as string,
      render: event => (
        <>{capitalizeRemoveUnderScores(event.resource_type)}</>
      ),
      renderFilter: function BasicStringFilter() {
        return (
          <SelectFilter
            items={AuditLogResourceTypeItems()}
            columnKey='resource_type'
            label={translate('reporting.auditEvents.resourceType.title') as string}
            minSearchLength={1}
          />
        )
      }
    },
    {
      key: 'description',
      title: translate('common.description') as string,
      render: event => (
        <>{event.description}</>
      )
    },
    {
      key: 'status',
      title: translate('common.form.status') as string,
      sorter: true,
      render: event => (
        <Tag type={event.status === 'success' ? TagType.ACTIVE : TagType.INACTIVE}>
          {sentenceCase(event.status.toString())}
        </Tag>
      ),
      renderFilter: function BasicStringFilter() {
        return (
          <SelectFilter
            multi={false}
            items={AuditLogStatusItems()}
            columnKey='status'
            label={translate('common.form.status') as string}
            minSearchLength={1}
          />
        )
      }
    },
    {
      key: 'client_ip',
      title: translate('common.form.clientIp') as string,
      sorter: false,
      render: event => (
        <>{event.client_ip}</>
      )
    }
  ]

  const parseApiFilter = (newMeta) => {
    if(newMeta.filters){
      const tmpArray: any = Object.getOwnPropertyNames(newMeta.filters).map((key: string) => {
        if (newMeta.filters) {
          const item = newMeta.filters[key]

          if (item) {
            // timestamp should be converted to utc format
            if(key === 'timestamp'){
              return {
                ['to']: moment(newMeta.filters.timestamp[0].value[1]).utc().format(),
                ['from']: moment(newMeta.filters.timestamp[0].value[0]).utc().format()
              }
            }

            // resource type allows for multiple values comma separated
            if (key === 'resource_type'){
              return {
                ['resourceType']: item.map(option => [option.value].join(','))
              }
            }

            // key account in api is accountId
            if (key === 'account'){
              return {
                ['accountId']: item[0].value
              }
            }

            // key status values should be uppercase
            if (key === 'status'){
              return {
                [key]: item[0].value.toUpperCase()
              }
            }

            else {
              return {
                [key]: item[0].value
              }
            }
          }
        }
      });

      return Object.assign({}, ...tmpArray)
    }

    // clear filters
    else {
      return Object.assign({}, {})
    }
  }

  const getEvents = useCallback((meta, onFirstMount?:boolean): Promise<any> => {
      const params: ISort = {limit: meta.pageSize, offset: meta.pageSize * (meta.currentPage - 1)}

      if (sortedInfo.order && sortedInfo.columnKey) {
        params.sort = [{
          columnKey: `${sortedInfo.columnKey}:${sortedInfo.order === 'descend' ? 'desc' : 'asc'}`
        }]
      }

      return fetchAuditEvents(params, parseApiFilter(meta)).then((response:any) => {
        if(onFirstMount) {
          setMeta({ ...meta, ...{totalItems: response.total} });
        }
        return response
      })
    },
    [fetchAuditEvents, sortedInfo.columnKey, sortedInfo.order]
  )

  const hideNotification = (id: string | number) => {
    setNotifications(notifications.filter((notification) => notification.id !== id));
  }

  const getRowActions = (item) => {
    return [
      {
        title: translate('reporting.auditEvents.checkDataIntegrityTooltip') as string,
        isVisible: item.audit_log_hash_id,
        onClick: () => handleCheckDataIntergrity(item.id),
        icon: <FontAwesomeIcon icon={faFileSearch}/>
      }
    ];
  };

  const tableChange = useCallback(
    (newMeta: TableMetaProps<IAuditLogsEvent>) => {
      let newSortedInfo = {
        columnKey: newMeta.sort?.columnKey,
        order: newMeta.sort?.order
      }

      // set to ascending by name if undefined
      if (newSortedInfo.columnKey === undefined && newSortedInfo.order === undefined) {
        newSortedInfo = {
          columnKey: 'timestamp',
          order: 'descend'
        }
      }

      // on sort go back to page 1
      if (!isEqual(newSortedInfo, sortedInfo)) {
        setCurrent(newMeta.currentPage = 1)
        setSortedInfo({
          columnKey: newMeta.sort?.columnKey || '',
          order: newMeta.sort?.order || ''
        })
      }

      if (!isEqual(newMeta.currentPage, current)) {
        setCurrent(newMeta.currentPage)
        setMeta({...newMeta, pageSize: newMeta.pageSize * (newMeta.currentPage - 1)})
      }

      if (!isEqual(newMeta.filters, meta.filters)) {
        setCurrent(newMeta.currentPage = 1)
        setMeta({...newMeta, filters: parseApiFilter(newMeta)})
      }

      setMeta({...newMeta})

      getEvents(newMeta).then((response) => {
        setEvents(response.items)
        setMeta({ ...newMeta, ...{totalItems: response.total} });
        setLoading(false)
      })
    },
    [current, getEvents, meta.filters, sortedInfo]
  )

  useEffect(() => {
    if(!loading) {
      const tableElement = document.getElementsByClassName('resizable-table-body')[0] as HTMLElement;
      if(tableElement){
        resizeTableBody(tableElement)
        window.addEventListener('resize', () => resizeTableBody(tableElement), false)
      }
      return (): void => {
        window.removeEventListener('resize', () => resizeTableBody(tableElement), false)
      }
    }
  }, [loading])

  useEffect(() => {
    const params: ISort = { limit: LIMIT_MAX, offset: 0 }
    if(accounts.length === 0) {
      fetchTenants(params).then((response) => {
        setAllAccounts(response.items)
      })
    }
  }, [accounts, fetchTenants])

  return (
    <section className={styles.reportingEvents}>
      <header>
        <h1><Translate id='reporting.auditEvents.title'/></h1>
      </header>
      <Table
        className={styles.table + ' ' +  'resizable-table-body'}
        columns={getTableColumns()}
        items={events}
        meta={meta}
        getRowKey={(event) => event.id}
        actionsButtonsQuantity={1}
        getActions={(item) => getRowActions(item)}
        onChange={tableChange}
        disableCheckBoxes
        loading={loading}
        saveDataName={'audit-table-' + profile?.id}
      />
      <ToasterContainer hideNotification={hideNotification} notifications={notifications} />
    </section>
  )
}

export default connect(
  (state: IAppState): IAuditLogsStateProps => ({
    accounts: state.profile.data ? state.profile.data.accounts : [],
    profile: state.profile?.data,
  }),
  {
    fetchAuditEvents,
    fetchTenants,
    checkDataIntegrity
  }
)(withPermissionsHOC(AuditLogs, ['canViewAuditLog']))
